// src/components/Welcome.js
// Public landing page — Maghribi horseshoe arch + البيان calligraphy.
// Everything is fluid: clamp()/vw/vh, no fixed pixel widths.
// Buttons are wired: دخول, إنشاء حساب, دعوة تجريبية, تبديل اللغة.

import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../contexts/ThemeContext';
import { setLocale, SUPPORTED_LOCALES } from '../i18n';
import { MaghribiHeading } from './sacred';

const LOCALE_LABELS = { ar: 'العربية', en: 'English' };

const Welcome = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const colorMode = useThemeContext();
  const [langAnchor, setLangAnchor] = useState(null);

  const handleLangClick = (e) => setLangAnchor(e.currentTarget);
  const handleLangClose = () => setLangAnchor(null);
  const handleLangChoose = (lang) => { setLocale(lang); handleLangClose(); };

  return (
    <Box
      className="medersa-pattern"
      sx={(th) => ({
        position: 'relative',
        minHeight: '100dvh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: th.palette.sacred.surface,
        px: 'clamp(1rem, 4vw, 3rem)',
        // Make the tessellation breathe across the whole page rather
        // than living as a faint corner accent — bumps the global
        // --motif-opacity for THIS page only so the brand zellige
        // motif reads at a glance on both light and dark surfaces.
        '--motif-opacity': '0.12',
        '--motif-size': '260px',
      })}
    >
      {/* Top-right text links */}
      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 3 }}
        sx={{ position: 'fixed', top: 'clamp(1rem, 2vw, 2rem)', insetInlineEnd: 'clamp(1rem, 3vw, 3rem)', zIndex: 10 }}
      >
        <Button
          onClick={() => navigate('/login')}
          sx={{ fontFamily: 'var(--sacred-font-body)', color: 'sacred.onSurfaceVariant', opacity: 0.75, '&:hover': { color: 'sacred.primary', opacity: 1 } }}
        >
          تسجيل الدخول
        </Button>
        <Button
          onClick={() => navigate('/signup')}
          sx={{ fontFamily: 'var(--sacred-font-body)', color: 'sacred.onSurfaceVariant', opacity: 0.75, '&:hover': { color: 'sacred.primary', opacity: 1 } }}
        >
          تأسيس كُتّاب
        </Button>
      </Stack>

      {/* Top-left icon buttons — functional */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ position: 'fixed', top: 'clamp(1rem, 2vw, 2rem)', insetInlineStart: 'clamp(1rem, 3vw, 3rem)', zIndex: 10 }}
      >
        <IconButton
          onClick={handleLangClick}
          sx={{ color: 'sacred.onSurfaceVariant' }}
          aria-label="Change language"
          title={LOCALE_LABELS[i18n.language] || i18n.language}
        >
          <span className="material-symbols-outlined">language</span>
        </IconButton>
        <IconButton
          onClick={colorMode.toggleColorMode}
          sx={{ color: 'sacred.onSurfaceVariant' }}
          aria-label="Toggle dark mode"
          title={colorMode.mode === 'dark' ? 'الوضع النهاري' : 'الوضع الليلي'}
        >
          <span className="material-symbols-outlined">
            {colorMode.mode === 'dark' ? 'light_mode' : 'dark_mode'}
          </span>
        </IconButton>
      </Stack>

      <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={handleLangClose}>
        {SUPPORTED_LOCALES.map((lng) => (
          <MenuItem
            key={lng}
            selected={i18n.language === lng}
            onClick={() => handleLangChoose(lng)}
            sx={{ fontFamily: 'var(--sacred-font-body)' }}
          >
            {LOCALE_LABELS[lng] || lng}
          </MenuItem>
        ))}
      </Menu>

      {/* Centered horseshoe card — fully fluid */}
      <Box
        sx={{
          position: 'relative', zIndex: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          width: '100%',
          maxWidth: 'min(440px, 92vw)',
        }}
      >
        <Box
          className="horseshoe"
          sx={(th) => ({
            width: '100%',
            aspectRatio: '380 / 580',
            maxHeight: '82dvh',
            backgroundColor: th.palette.sacred.surfaceContainerLowest,
            boxShadow: `0 2.5vw 6vw -1vw ${th.palette.sacred.primary}1F`,
            position: 'relative',
            overflow: 'hidden',
          })}
        >
          <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'sacred.surfaceContainerLow', opacity: 0.4 }} />

          <Box
            sx={{
              position: 'relative',
              height: '100%', width: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              px: 'clamp(1.5rem, 6%, 3rem)',
              py: 'clamp(2rem, 8%, 4rem)',
              gap: 'clamp(1rem, 3vh, 2rem)',
            }}
          >
            <MaghribiHeading
              variant="display" glow
              sx={{
                fontSize: 'clamp(3.5rem, 12vw, 6rem)',
                lineHeight: 1,
                userSelect: 'none',
              }}
            >
              البيان
            </MaghribiHeading>

            <Typography
              sx={{
                fontFamily: 'var(--sacred-font-display)',
                fontSize: 'clamp(1rem, 2.5vw, 1.4rem)',
                color: 'sacred.onSurfaceVariant',
                textAlign: 'center', lineHeight: 1.6,
              }}
            >
              نُورُ الحفظ، بيانُ الفهم
            </Typography>

<Stack spacing={1.25} sx={{ width: '100%', mt: 1 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
                sx={{
                  py: 'clamp(0.75rem, 2vw, 1.25rem)',
                  fontSize: 'clamp(1rem, 2vw, 1.15rem)',
                  borderRadius: 2,
                  fontFamily: 'var(--sacred-font-body)',
                }}
              >
                دخول
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => navigate('/signup')}
                sx={{
                  py: 'clamp(0.6rem, 1.5vw, 1rem)',
                  fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
                  borderRadius: 2,
                  fontFamily: 'var(--sacred-font-body)',
                  color: 'sacred.primary', borderColor: 'sacred.outlineVariant',
                  '&:hover': { borderColor: 'sacred.primary', bgcolor: 'sacred.surfaceContainerLow' },
                }}
              >
                تأسيس كُتّاب جديد
              </Button>
            </Stack>
          </Box>

          {/* (corner motif removed — the page-wide tessellation handles it) */}
        </Box>

        {/* Emerald halo */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute', bottom: '-2vh',
            width: '60%', height: '12%',
            bgcolor: 'sacred.primary', opacity: 0.05,
            filter: 'blur(80px)', borderRadius: '50%', zIndex: -1,
          }}
        />
      </Box>

    </Box>
  );
};

export default Welcome;
